// src/pages/AdminDashboard.js
import React, { useEffect, useState } from 'react';
import { getPortfolioItems, createPortfolio, deletePortfolio, uploadPortfolioImage } from '../api/portfolioApi';
import { getOfferings, createOffering, deleteOffering } from '../api/offeringsApi';
import { getJobRoles, createJobRole, updateJobRole, deleteJobRole } from '../api/careersApi';
import LoadingSpinner from '../components/LoadingSpinner';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('portfolio');

  // Portfolio states
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [newPortfolio, setNewPortfolio] = useState({ title: '', description: '', techStack: '', link: '', image: null });
  const [portfolioMsg, setPortfolioMsg] = useState({ type: '', text: '' });

  // Offerings states
  const [offerings, setOfferings] = useState([]);
  const [offeringsLoading, setOfferingsLoading] = useState(true);
  const [newOffering, setNewOffering] = useState({ name: '', description: '', link: '' });
  const [offeringMsg, setOfferingMsg] = useState({ type: '', text: '' });

  // Jobs states
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [newJob, setNewJob] = useState({
    title: '',
    summary: '',
    description: '',
    requirements: '',
    benefits: '',
    aboutCompany: '',
    location: '',
    type: '',
    salary: '',
    active: true,
  });
  const [jobMsg, setJobMsg] = useState({ type: '', text: '' });
  const [editingJobId, setEditingJobId] = useState(null);

  useEffect(() => {
    fetchPortfolio();
    fetchOfferings();
    fetchJobs();
  }, []);

  // === Portfolio Methods ===
  const fetchPortfolio = () => {
    setPortfolioLoading(true);
    getPortfolioItems()
      .then(res => setPortfolioItems(res.data))
      .catch(console.error)
      .finally(() => setPortfolioLoading(false));
  };

  const handlePortfolioDelete = id => {
    if (!window.confirm('Delete this portfolio item?')) return;
    deletePortfolio(id)
      .then(() => {
        setPortfolioMsg({ type: 'success', text: 'Portfolio item deleted successfully!' });
        fetchPortfolio();
      })
      .catch(() => setPortfolioMsg({ type: 'error', text: 'Failed to delete portfolio item.' }));
  };

  const handlePortfolioCreate = async e => {
    e.preventDefault();
    try {
      let imageUrl = '';
      if (newPortfolio.image) {
        const res = await uploadPortfolioImage(newPortfolio.image);
        imageUrl = res.data || '';
      }
      await createPortfolio({ ...newPortfolio, techStack: newPortfolio.techStack.split(','), imageUrl });
      setNewPortfolio({ title: '', description: '', techStack: '', link: '', image: null });
      fetchPortfolio();
      setPortfolioMsg({ type: 'success', text: 'Portfolio item added successfully!' });
    } catch (err) {
      console.error(err);
      setPortfolioMsg({ type: 'error', text: 'Failed to add portfolio item.' });
    }
  };

  // === Offerings Methods ===
  const fetchOfferings = () => {
    setOfferingsLoading(true);
    getOfferings()
      .then(res => setOfferings(res.data))
      .catch(console.error)
      .finally(() => setOfferingsLoading(false));
  };

  const handleOfferingDelete = id => {
    if (!window.confirm('Delete this service?')) return;
    deleteOffering(id)
      .then(() => {
        setOfferingMsg({ type: 'success', text: 'Service deleted successfully!' });
        fetchOfferings();
      })
      .catch(() => setOfferingMsg({ type: 'error', text: 'Failed to delete service.' }));
  };

  const handleOfferingCreate = async e => {
    e.preventDefault();
    try {
      await createOffering(newOffering);
      setNewOffering({ name: '', description: '', link: '' });
      fetchOfferings();
      setOfferingMsg({ type: 'success', text: 'Service added successfully!' });
    } catch (err) {
      console.error(err);
      setOfferingMsg({ type: 'error', text: 'Failed to add service.' });
    }
  };

  // === Jobs Methods ===
  const fetchJobs = () => {
    setJobsLoading(true);
    getJobRoles()
      .then(res => setJobs(res.data))
      .catch(console.error)
      .finally(() => setJobsLoading(false));
  };

  const handleJobSubmit = async e => {
    e.preventDefault();
    try {
      if (editingJobId) {
        await updateJobRole(editingJobId, newJob);
        setJobMsg({ type: 'success', text: 'Job updated successfully!' });
        setEditingJobId(null);
      } else {
        await createJobRole(newJob);
        setJobMsg({ type: 'success', text: 'Job added successfully!' });
      }
      setNewJob({
        title: '',
        summary: '',
        description: '',
        requirements: '',
        benefits: '',
        aboutCompany: '',
        location: '',
        type: '',
        salary: '',
        active: true,
      });
      fetchJobs();
    } catch (err) {
      console.error(err);
      setJobMsg({ type: 'error', text: 'Failed to save job.' });
    }
  };

  const handleEditJob = job => {
    setEditingJobId(job.id);
    setNewJob({ ...job });
  };

  const handleDeleteJob = async id => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await deleteJobRole(id);
      setJobMsg({ type: 'success', text: 'Job deleted successfully!' });
      fetchJobs();
    } catch (err) {
      console.error(err);
      setJobMsg({ type: 'error', text: 'Failed to delete job.' });
    }
  };

  const handleToggleActive = async job => {
    try {
      await updateJobRole(job.id, { ...job, active: !job.active });
      fetchJobs();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="admin-dashboard-container">
      <h2 className="dashboard-title">Admin Dashboard</h2>

      <div className="tab-buttons">
        <button className={`tab-btn ${activeTab === 'portfolio' ? 'active' : ''}`} onClick={() => setActiveTab('portfolio')}>Portfolio</button>
        <button className={`tab-btn ${activeTab === 'offerings' ? 'active' : ''}`} onClick={() => setActiveTab('offerings')}>Services</button>
        <button className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}>Jobs</button>
        <div className={`tab-underline ${activeTab}`}></div>
      </div>

      <div className="tab-slider">
        {/* Portfolio Tab */}
        <div className={`tab-content ${activeTab === 'portfolio' ? 'active' : 'inactive'}`}>
          <div className="form-wrapper">
            <h3 className="form-heading">Add New Portfolio Item</h3>
            {portfolioMsg.text && <div className={`form-msg ${portfolioMsg.type}`}>{portfolioMsg.text}</div>}
            <form onSubmit={handlePortfolioCreate} className="admin-form">
              <input type="text" placeholder="Title" value={newPortfolio.title} onChange={e => setNewPortfolio({ ...newPortfolio, title: e.target.value })} required />
              <input type="text" placeholder="Description" value={newPortfolio.description} onChange={e => setNewPortfolio({ ...newPortfolio, description: e.target.value })} required />
              <input type="text" placeholder="Tech Stack (comma separated)" value={newPortfolio.techStack} onChange={e => setNewPortfolio({ ...newPortfolio, techStack: e.target.value })} />
              <input type="text" placeholder="Project Link" value={newPortfolio.link} onChange={e => setNewPortfolio({ ...newPortfolio, link: e.target.value })} />
              <input type="file" onChange={e => setNewPortfolio({ ...newPortfolio, image: e.target.files[0] })} />
              <button type="submit" className="submit-btn">Add Portfolio</button>
            </form>
          </div>

          <h3 className="section-heading">Existing Portfolio</h3>
          {portfolioLoading ? <LoadingSpinner /> : (
            <div className="cards-container">
              {portfolioItems.length ? portfolioItems.map(item => (
                <div key={item.id} className="admin-card">
                  {item.imageUrl && <img src={item.imageUrl} alt={item.title} className="admin-card-img" />}
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                  <p><strong>Tech:</strong> {item.techStack?.join(', ')}</p>
                  {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer">Project Link</a>}
                  <button className="delete-btn" onClick={() => handlePortfolioDelete(item.id)}>Delete</button>
                </div>
              )) : <p>No portfolio items yet.</p>}
            </div>
          )}
        </div>

        {/* Services Tab */}
        <div className={`tab-content ${activeTab === 'offerings' ? 'active' : 'inactive'}`}>
          <div className="form-wrapper">
            <h3 className="form-heading">Add New Service</h3>
            {offeringMsg.text && <div className={`form-msg ${offeringMsg.type}`}>{offeringMsg.text}</div>}
            <form onSubmit={handleOfferingCreate} className="admin-form">
              <input type="text" placeholder="Name" value={newOffering.name} onChange={e => setNewOffering({ ...newOffering, name: e.target.value })} required />
              <input type="text" placeholder="Description" value={newOffering.description} onChange={e => setNewOffering({ ...newOffering, description: e.target.value })} required />
              <input type="text" placeholder="Link" value={newOffering.link} onChange={e => setNewOffering({ ...newOffering, link: e.target.value })} />
              <button type="submit" className="submit-btn">Add Service</button>
            </form>
          </div>

          <h3 className="section-heading">Existing Services</h3>
          {offeringsLoading ? <LoadingSpinner /> : (
            <div className="cards-container">
              {offerings.length ? offerings.map(item => (
                <div key={item.id} className="admin-card">
                  <h4>{item.name}</h4>
                  <p>{item.description}</p>
                  {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer">Link</a>}
                  <button className="delete-btn" onClick={() => handleOfferingDelete(item.id)}>Delete</button>
                </div>
              )) : <p>No services yet.</p>}
            </div>
          )}
        </div>

        {/* Jobs Tab */}
        <div className={`tab-content ${activeTab === 'jobs' ? 'active' : 'inactive'}`}>
          <div className="form-wrapper">
            <h3 className="form-heading">{editingJobId ? 'Edit Job' : 'Add New Job'}</h3>
            {jobMsg.text && <div className={`form-msg ${jobMsg.type}`}>{jobMsg.text}</div>}
            <form onSubmit={handleJobSubmit} className="admin-form">
              <input type="text" placeholder="Job Title" value={newJob.title} onChange={e => setNewJob({ ...newJob, title: e.target.value })} required />
              <input type="text" placeholder="Summary" value={newJob.summary} onChange={e => setNewJob({ ...newJob, summary: e.target.value })} required />
              <textarea placeholder="Description" value={newJob.description} onChange={e => setNewJob({ ...newJob, description: e.target.value })} required />
              <textarea placeholder="Requirements" value={newJob.requirements} onChange={e => setNewJob({ ...newJob, requirements: e.target.value })} />
              <textarea placeholder="Benefits" value={newJob.benefits} onChange={e => setNewJob({ ...newJob, benefits: e.target.value })} />
              <textarea placeholder="About Company" value={newJob.aboutCompany} onChange={e => setNewJob({ ...newJob, aboutCompany: e.target.value })} />
              <input type="text" placeholder="Location" value={newJob.location} onChange={e => setNewJob({ ...newJob, location: e.target.value })} />
              <input type="text" placeholder="Type (Full-time/Part-time)" value={newJob.type} onChange={e => setNewJob({ ...newJob, type: e.target.value })} />
              <input type="text" placeholder="Salary" value={newJob.salary} onChange={e => setNewJob({ ...newJob, salary: e.target.value })} />
              <label>
                <input type="checkbox" checked={newJob.active} onChange={e => setNewJob({ ...newJob, active: e.target.checked })} />
                Active
              </label>
              <button type="submit" className="submit-btn">{editingJobId ? 'Update Job' : 'Add Job'}</button>
            </form>
          </div>

          <h3 className="section-heading">Existing Jobs</h3>
          {jobsLoading ? <LoadingSpinner /> : (
            <div className="cards-container">
              {jobs.length ? jobs.map(job => (
                <div key={job.id} className="admin-card">
                  <h4>{job.title}</h4>
                  <p>{job.summary}</p>
                  <p><strong>Location:</strong> {job.location} | <strong>Type:</strong> {job.type} | <strong>Status:</strong> {job.active ? 'Active' : 'Inactive'}</p>
                  <button className="submit-btn" onClick={() => handleEditJob(job)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDeleteJob(job.id)}>Delete</button>
                  <button className="submit-btn" onClick={() => handleToggleActive(job)}>
                    {job.active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              )) : <p>No jobs yet.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;